import { rootNamespace } from "config";
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  createElement,
} from "react";
import { TreeContext } from "./Editor";
import _ from "lodash";
import Window, { computePosition } from "lib/Window";
import { Tab } from "lib/Window/Tabs";
import windowMap, { defaultDimensions } from "windows";
import { addChild, removeChild, removeLastIndex } from "lib/helpers";
import { generateUID } from "lib/PubSub";
import update from "immutability-helper";

export const WindowContext = createContext();

function findWindow(windows, tab) {
  let window;
  for (let i in windows) {
    const tabName = typeof tab === "object" ? tab.name : tab;
    const found = _.find(windows[i].tabs, ["name", tabName]);
    if (found) {
      window = i;
      break;
    }
  }
  return window;
}

function removeTab(windows, tab) {
  let updatedWindows = windows;
  Object.entries(windows).forEach(([k, w]) => {
    const idx = _.findIndex(w.tabs, ["name", tab.name]);
    if (idx > -1) {
      const removeKey = w.tabs.length === 1 ? k : k + ".tabs." + idx;
      updatedWindows = removeChild(updatedWindows, removeKey);
    }
  });
  return updatedWindows;
}

export default function Windows({ layout }) {
  const ref = useRef();
  const [dimensions, setDimensions] = useState();
  const [stack, setStack] = useState([]);
  const [windows, setWindows] = useState({});
  const { subscribe } = useContext(TreeContext);
  useEffect(() => {
    return subscribe(rootNamespace, "FOCUS", (title) => {
      // updated
      let uStack = stack;
      if (stack.includes(title)) {
        uStack = stack.filter((t) => t !== title);
      }
      setStack([...uStack, title]);
    });
  }, [stack, subscribe]);
  useEffect(() => {
    return subscribe(rootNamespace, "DISABLE", (data) => {
      const updatePath = {};
      for (let i in windows) {
        const key = data.target === i ? "disable" : "dragging";
        const val =
          key === "dragging" && data.disable !== false ? true : data.disable;
        _.set(updatePath, [i, key, "$set"].join("."), val);
      }
      setWindows(update(windows, updatePath));
      setStack(stack.filter((t) => t !== data.target).concat(data.target));
    });
  }, [stack, subscribe, windows]);
  useEffect(() => {
    return subscribe(rootNamespace, "FOCUS_TAB", (tab) => {
      let wKey = findWindow(windows, tab);
      // focus tab
      let path;
      if (wKey) {
        // focus opened tab
        const window = windows[wKey];
        const idx = _.findIndex(window.tabs, ["name", tab]);
        path = _.set({}, wKey + ".tab", { $set: idx });
      } else {
        // reopen closed tab
        const id = (wKey = generateUID());
        let pos = windowMap[tab].dimensions;
        if (!pos) pos = defaultDimensions;
        pos = computePosition(pos, dimensions);
        const newTab = { name: tab };
        path = _.set({}, id, { $set: { tabs: [newTab], pos } });
      }
      setWindows(update(windows, path));
      setStack([...stack.filter((w) => w !== wKey), wKey]);
    });
  }, [dimensions, stack, subscribe, windows]);
  useEffect(() => {
    if (!dimensions) return;
    setStack(Object.keys(layout));
    const windows = _.mapValues(layout, ({ pos, tabs }) => {
      return {
        pos: computePosition(pos, dimensions),
        tabs,
      };
    });
    setWindows(windows);
    // TODO: add resize handler
  }, [layout, dimensions]);
  useLayoutEffect(() => {
    setDimensions({
      width: ref.current.offsetWidth,
      height: ref.current.offsetHeight,
    });
  }, []);
  function attachTab(tab, path) {
    // check if window already has the same tab
    // id prop for <Window /> & pos prop for <Tab /> has been
    // added for the same purpose
    let updatedWindows = windows;
    updatedWindows = removeTab(updatedWindows, tab);
    updatedWindows = addChild(updatedWindows, tab, path);
    // remove dragging flags from all windows
    const updatePath = {};
    for (let i in updatedWindows) {
      _.set(updatePath, i + ".$unset", ["dragging"]);
    }
    updatedWindows = update(updatedWindows, updatePath);
    setWindows(updatedWindows);
  }
  /**
   *
   * @param {string} path
   * @param {object} pos x, y position
   */
  function detachTab(path, pos) {
    const tab = _.get(windows, path);
    const ppath = removeLastIndex(path);
    let tabs = _.get(windows, ppath);
    let updatedWindows = windows;
    const window = path.split(".")[0];
    const removePath = _.set({}, ppath, {
      $set: [tab],
    });
    updatedWindows = update(updatedWindows, removePath);
    tabs = tabs.filter((t) => t !== tab);
    const id = generateUID();
    updatedWindows = addChild(
      updatedWindows,
      {
        tabs,
        pos,
        dragging: true,
      },
      id
    );
    setWindows(updatedWindows);
    setStack([...stack.filter((w) => w !== window), id, window]);
  }
  function closeTab(tab) {
    let updatedWindows = removeTab(windows, tab);
    setWindows(updatedWindows);
  }
  function focusTab(tab) {
    const window = findWindow(windows, tab);
    const path = _.set({}, window + ".tab", { $set: tab });
    setWindows(update(windows, path));
  }
  function setPosition(key) {
    return (pos) => {
      const updatePath = {};
      for (let i in pos) {
        if (pos[i]) {
          _.set(updatePath, [key, "pos", i, "$set"].join("."), pos[i]);
        }
      }
      for (let i in windows) {
        _.set(updatePath, i + ".$unset", ["dragging"]);
      }
      setWindows(update(windows, updatePath));
    };
  }
  function setDragging(key, dragging) {
    const updatePath = _.set({}, key + ".dragging.$set", dragging);
    setWindows(update(windows, updatePath));
  }
  const tabs = {};
  Object.values(windows).forEach((f) => {
    f.tabs.forEach((t) => {
      const w = createElement(windowMap[t.name].component);
      tabs[t.name] = (
        <Tab key={t.name} title={t.name} {...windowMap[t.name].props}>
          {w}
        </Tab>
      );
    });
  });
  return (
    <div className="wc" {...{ ref }}>
      {Object.entries(windows).map(([key, f]) => {
        const windowApi = {
          index: stack.indexOf(key),
          name: key,
          detachTab: (index, pos) => detachTab(key + ".tabs." + index, pos),
          attachTab: (tab, index) => attachTab(tab, key + ".tabs." + index),
          closeTab,
          setPosition: setPosition(key),
          focusTab,
        };
        return (
          <WindowContext.Provider value={windowApi} key={key}>
            <Window
              id={key}
              style={f.pos}
              activeTab={f.tab}
              // used to disable pointer events on other windows
              // to prevent interference
              dragging={f.dragging}
              setDragging={(dragging) => setDragging(key, dragging)}
              // used to disable entire window
              // and also used by tab for setting up listeners
              // for attach & detach
              disable={f.disable}
            >
              {f.tabs.map((t) => tabs[t.name])}
            </Window>
          </WindowContext.Provider>
        );
      })}
    </div>
  );
}
